import { Injectable, NgZone } from '@angular/core';
import {
    EndSensitivity,
    GoogleGenAI,
    Modality,
    StartSensitivity,
} from '@google/genai';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiService } from '../api.service';
import { McpContextService } from '../mcp-context.service';
import { McpRealtimeStatus, McpTranscriptEntry } from '../mcp-realtime.service';
import { McpTraceService } from '../mcp-trace.service';
import { PageActionService } from '../page-action.service';
import { McpIntentRouterService } from '../mcp-intent-router.service';
import { RealtimeSegment } from './realtime-segment.types';

/**
 * GeminiLiveSegment ("gemini2")
 *
 * A deliberately minimal, documentation-faithful Gemini Live client for
 * `gemini-3.1-flash-live-preview`. It exists ALONGSIDE the original
 * `GeminiRealtimeSegment` ('gemini') so the two can be A/B compared from the
 * kiosk engine toggle. Nothing here touches the original segment.
 *
 * It implements ONLY what the Gemini Live API docs prescribe:
 *   - Connect on `v1alpha` with AUDIO modality + input/output transcription.
 *   - Server-side automatic VAD (tunable via realtimeInputConfig).
 *   - Mic audio streamed as 16 kHz PCM16 via `sendRealtimeInput({ audio })`.
 *   - `sendRealtimeInput({ audioStreamEnd: true })` when the mic is paused.
 *   - Text sent via `sendRealtimeInput({ text })` (3.1 does NOT accept
 *     `sendClientContent` mid-conversation).
 *   - Playback of 24 kHz PCM output, cleared on `serverContent.interrupted`.
 *   - Function calling via `sendToolResponse`.
 *
 * Intentionally OMITTED (these were the band-aids in the old segment):
 *   wake-word SpeechRecognition, silence nudges, stuck-state watchdog prompts,
 *   turn-count refreshes, and live board context pushes.
 */
@Injectable({ providedIn: 'root' })
export class GeminiLiveSegment implements RealtimeSegment {
    readonly id = 'gemini2' as const;

    private readonly _status = new BehaviorSubject<McpRealtimeStatus>('idle');
    private readonly _transcript = new BehaviorSubject<McpTranscriptEntry[]>([]);
    private readonly _aiSpeaking = new BehaviorSubject<boolean>(false);
    private readonly _micMuted = new BehaviorSubject<boolean>(false);
    private readonly _userSpeaking = new BehaviorSubject<boolean>(false);
    private readonly _inputLevel = new BehaviorSubject<number>(0);
    private _meterFrame = 0;
    /**
     * Fires when the user asks (by voice) to end the visit. This build has no
     * wake word to fall back to, so ending = fully close the overlay; the kiosk
     * component listens and calls deactivate().
     */
    private readonly _endRequested = new Subject<void>();

    private _session: any = null;
    private _connectAttemptId = 0;
    private _autoReconnect = false;

    // Mic capture
    private _micStream: MediaStream | null = null;
    private _inputCtx: AudioContext | null = null;
    private _inputSource: MediaStreamAudioSourceNode | null = null;
    private _inputProcessor: ScriptProcessorNode | null = null;

    // Output playback
    private _outputCtx: AudioContext | null = null;
    private _nextPlaybackTime = 0;
    private _activeSources = new Set<AudioBufferSourceNode>();
    private _playbackEndTimer: any = null;

    // Turn assembly
    private _assistantTurnActive = false;
    private _lastInputTranscript = '';

    // Debug diagnostics (temporary — flip DEBUG off once hearing is confirmed).
    private static readonly DEBUG = true;
    private _frameCount = 0;
    private _msgCount = 0;
    private _winPeak = 0;

    // Session lifetime: audio-only Live sessions are capped at 15 min; refresh
    // a little early so a conversation never dies mid-sentence.
    private _refreshTimer: any = null;
    private _reconnectTimer: any = null;
    private static readonly SESSION_REFRESH_MS = 14 * 60 * 1000;
    private static readonly INPUT_TARGET_RATE = 16000;
    private static readonly OUTPUT_RATE = 24000;
    private static readonly POST_PLAYBACK_GAP_S = 0.15;
    // Local RMS voice meter (independent of server VAD) — drives the "heard" pulse.
    private static readonly SPEAK_ON_RMS = 0.015;
    private static readonly SPEAK_OFF_RMS = 0.008;

    // Voice intents that end the visit (all treated the same: close the overlay).
    private static readonly END_ACTIONS = new Set([
        'end_conversation',
        'end_session',
        'end_call',
        'finish_conversation',
        'go_to_sleep',
        'mute_mic',
        'mute',
        'stop',
        'goodbye',
    ]);
    // Delay before we sleep so the model's spoken goodbye can finish.
    private static readonly END_GRACE_MS = 2200;
    private _endTimer: any = null;
    private _endPending = false;

    // Wake-word (Option B): after "that's all" the session stays CONNECTED but
    // muted; the browser's local SpeechRecognition listens for a wake word and
    // un-mutes the Gemini mic on a match. Nothing streams to Google while idle.
    private static readonly WAKE_WORDS = ['yo', 'hey assistant', 'hey gemini', 'hey system', 'computer'];
    private _wakeWordRecognition: any = null;
    /** When true, the next connect lands "asleep" (muted) and waits for a wake word. */
    private _requireWakeWordOnConnect = false;

    constructor(
        private readonly zone: NgZone,
        private readonly api: ApiService,
        private readonly pageActionService: PageActionService,
        private readonly contextService: McpContextService,
        private readonly traceService: McpTraceService,
        private readonly intentRouter: McpIntentRouterService
    ) {}

    get status$(): Observable<McpRealtimeStatus> {
        return this._status.asObservable();
    }
    get transcript$(): Observable<McpTranscriptEntry[]> {
        return this._transcript.asObservable();
    }
    get aiSpeaking$(): Observable<boolean> {
        return this._aiSpeaking.asObservable();
    }
    get micMuted$(): Observable<boolean> {
        return this._micMuted.asObservable();
    }
    get userSpeaking$(): Observable<boolean> {
        return this._userSpeaking.asObservable();
    }
    get inputLevel$(): Observable<number> {
        return this._inputLevel.asObservable();
    }
    get endRequested$(): Observable<void> {
        return this._endRequested.asObservable();
    }

    /**
     * Kiosk: true = land "asleep" (muted) on connect and wait for a wake word
     * before opening the mic; false = open the mic immediately.
     */
    prepareKioskSession(requireWakeWord: boolean): void {
        this._requireWakeWordOnConnect = requireWakeWord;
    }

    private log(...args: any[]): void {
        if (GeminiLiveSegment.DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[Gemini2]', ...args);
        }
    }

    async start(): Promise<void> {
        if (this._status.value === 'connecting' || this._status.value === 'active') {
            return;
        }
        this._autoReconnect = true;

        const snapshot = this.contextService.getSnapshot();
        if (!this.canStartOnCurrentPage(snapshot)) {
            this._status.next('idle');
            this.addTranscript(
                'system',
                'Assistant is available on mapped pages only (Receiving, Shipping, Printer View).'
            );
            return;
        }

        this._status.next('connecting');
        const attemptId = ++this._connectAttemptId;
        this.log('start(): fetching live-config…', { page: snapshot?.pageLabel, route: snapshot?.route });

        try {
            const promptContext = this.contextService.buildPromptContext(snapshot);
            const cfg: any = await this.api
                .postApi('mcp/gemini/live-config', {
                    context: {
                        prompt_context: promptContext,
                        route: snapshot.route,
                        page_label: snapshot.pageLabel,
                        user_name: snapshot.userName,
                        user_role: snapshot.role,
                        anonymous: snapshot.anonymous,
                    },
                })
                .toPromise();

            this.log('live-config response:', {
                hasKey: !!cfg?.api_key,
                model: cfg?.model,
                voice: cfg?.voice,
                tools: Array.isArray(cfg?.tools) ? cfg.tools.length : 0,
                systemInstructionChars: String(cfg?.system_instruction ?? '').length,
            });

            if (!cfg?.api_key || !cfg?.model) {
                throw new Error('Invalid Gemini live config response');
            }
            if (attemptId !== this._connectAttemptId) return;
            this.log('connecting to Live API…', { model: cfg.model });

            const ai = new GoogleGenAI({
                apiKey: cfg.api_key,
                // Live API features require the v1alpha surface.
                httpOptions: { apiVersion: 'v1alpha' },
            });
            const voiceName = String(cfg.voice ?? 'Zephyr');

            this._session = await ai.live.connect({
                model: String(cfg.model),
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: {
                        voiceConfig: { prebuiltVoiceConfig: { voiceName } },
                    },
                    systemInstruction: String(cfg.system_instruction ?? ''),
                    tools: Array.isArray(cfg.tools) ? cfg.tools : [],
                    inputAudioTranscription: {},
                    outputAudioTranscription: {},
                    // Automatic server VAD, tuned to catch speech readily and
                    // tolerate natural mid-sentence pauses (docs recommend
                    // 500-800ms silence). High start-sensitivity helps in a
                    // noisy warehouse; 700ms silence avoids fragmenting turns.
                    realtimeInputConfig: {
                        automaticActivityDetection: {
                            disabled: false,
                            startOfSpeechSensitivity:
                                StartSensitivity.START_SENSITIVITY_HIGH,
                            endOfSpeechSensitivity:
                                EndSensitivity.END_SENSITIVITY_LOW,
                            prefixPaddingMs: 300,
                            silenceDurationMs: 700,
                        },
                    },
                } as any,
                callbacks: {
                    onopen: () => this.zone.run(() => this.onOpen(attemptId)),
                    onmessage: (e: any) => this.zone.run(() => this.onMessage(e)),
                    onerror: (e: any) => this.zone.run(() => this.onError(attemptId, e)),
                    onclose: (ev: any) => this.zone.run(() => this.onClose(attemptId, ev)),
                },
            });

            // Acquire the mic AFTER connect resolves so `this._session` is set.
            // (The SDK can fire `onopen` before this assignment, so starting the
            // mic inside onopen races against a null session and no-ops silently.)
            if (attemptId !== this._connectAttemptId) return;
            this.log('connected; acquiring microphone…');
            try {
                await this.startMicStreaming();
            } catch (micErr) {
                console.error('[Gemini2] mic acquisition failed:', micErr);
                this.addTranscript(
                    'system',
                    'Microphone was blocked — allow the mic in the browser and reload.'
                );
            }
        } catch (err: any) {
            if (attemptId !== this._connectAttemptId) return;
            console.error('[Gemini2] start failed:', {
                message: err?.message ?? String(err),
                httpStatus: err?.status ?? err?.statusCode,
                serverError: err?.error?.error ?? err?.error,
            }, err);
            this._status.next('error');
            this.addTranscript('system', 'Assistant failed to start. Tap to retry.');
            this.traceService.log(
                'gemini2_start_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to start Gemini Live (gemini2)' }
            );
        }
    }

    async prewarm(): Promise<void> {
        if (this._status.value === 'active' || this._status.value === 'connecting') {
            return;
        }
        await this.start();
    }

    stop(): void {
        this._autoReconnect = false;
        this.teardown();
        this._status.next('idle');
    }

    setMicMuted(muted: boolean): void {
        if (this._micMuted.value === muted) return;
        this._micMuted.next(muted);
        if (muted) {
            // Per docs: flush any cached audio when the stream is paused.
            this.flushAudioStream();
            this._userSpeaking.next(false);
            this._inputLevel.next(0);
        }
    }

    toggleMic(): void {
        this.setMicMuted(!this._micMuted.value);
    }

    sendText(text: string): void {
        const trimmed = String(text ?? '').trim();
        if (!trimmed || this._status.value !== 'active' || !this._session) return;
        this.addTranscript('user', trimmed);
        try {
            // 3.1: mid-conversation text goes through send_realtime_input.
            this._session.sendRealtimeInput({ text: trimmed });
        } catch (err) {
            console.error('[Gemini2] sendText error:', err);
        }
    }

    // ---- session lifecycle ------------------------------------------------

    private onOpen(attemptId: number): void {
        if (attemptId !== this._connectAttemptId) return;
        this.log('onopen: session ACTIVE');
        this._frameCount = 0;
        this._msgCount = 0;
        this._status.next('active');
        this._aiSpeaking.next(false);
        this.ensureOutputContext();
        this.armSessionRefresh();
        // Mic is acquired in start() after connect resolves — not here — to
        // avoid the onopen/session-assignment race that left the mic dead.
        if (this._requireWakeWordOnConnect) {
            // Land asleep: mic muted, local wake-word listener on. Nothing
            // streams to Gemini until someone says a wake word.
            this._micMuted.next(true);
            this.startWakeWordListener();
            this.log('onopen: ASLEEP — waiting for wake word');
        } else {
            this._micMuted.next(false);
            this.addTranscript('system', 'Listening — just talk.');
        }
    }

    private onMessage(e: any): void {
        const content = e?.serverContent;

        if (GeminiLiveSegment.DEBUG) {
            this._msgCount++;
            const parts = content?.modelTurn?.parts;
            const audioParts = Array.isArray(parts)
                ? parts.filter((p: any) => p?.inlineData?.data).length
                : 0;
            this.log(`<- msg #${this._msgCount}`, {
                interrupted: !!content?.interrupted,
                inputTx: content?.inputTranscription?.text ?? null,
                outputTx: content?.outputTranscription?.text ?? null,
                parts: Array.isArray(parts) ? parts.length : 0,
                audioParts,
                turnComplete: !!content?.turnComplete,
                toolCalls: Array.isArray(e?.toolCall?.functionCalls)
                    ? e.toolCall.functionCalls.length
                    : 0,
                goAway: !!e?.goAway,
                keys: Object.keys(e ?? {}),
            });
        }

        // Barge-in / interruption: stop and empty the playback queue (docs).
        if (content?.interrupted) {
            this.clearPlayback();
            this._assistantTurnActive = false;
        }

        // User speech transcription.
        const inputText = String(content?.inputTranscription?.text ?? '').trim();
        if (inputText && inputText !== this._lastInputTranscript) {
            this._lastInputTranscript = inputText;
            this.addTranscript('user', inputText);
        }

        // Assistant text (from output transcription). 3.1 may pack multiple
        // parts per event, so read transcription and all text parts.
        const parts = content?.modelTurn?.parts;
        const partText = Array.isArray(parts)
            ? parts.map((p: any) => String(p?.text ?? '').trim()).filter(Boolean).join(' ')
            : '';
        const outputText =
            String(content?.outputTranscription?.text ?? '').trim() || partText;
        if (outputText) {
            if (this._assistantTurnActive) {
                this.appendAssistant(outputText);
            } else {
                this._assistantTurnActive = true;
                this.addTranscript('assistant', outputText);
            }
        }

        // Audio output.
        if (Array.isArray(parts)) {
            for (const part of parts) {
                const b64 = String(part?.inlineData?.data ?? '').trim();
                if (b64) {
                    const mime = String(part?.inlineData?.mimeType ?? '').toLowerCase();
                    this.playPcm16(b64, mime);
                }
            }
        }

        if (content?.turnComplete) {
            this._assistantTurnActive = false;
            this._lastInputTranscript = '';
        }

        // Function calling.
        const calls = e?.toolCall?.functionCalls;
        if (Array.isArray(calls) && calls.length > 0) {
            this.handleToolCalls(calls);
        }

        // Server asked us to refresh soon.
        if (e?.goAway) {
            this.reconnect();
        }
    }

    private onError(attemptId: number, e: any): void {
        if (attemptId !== this._connectAttemptId) return;
        console.error('[Gemini2] onerror:', e?.message ?? e, e);
        this._session = null;
        this._status.next('idle');
        if (this._autoReconnect) {
            this.addTranscript('system', 'Connection error — reconnecting…');
            this.scheduleReconnect();
        }
    }

    private onClose(attemptId: number, ev: any): void {
        if (attemptId !== this._connectAttemptId) return;
        console.warn('[Gemini2] closed:', ev?.code, ev?.reason);
        this._session = null;
        this._aiSpeaking.next(false);
        this._status.next('idle');
        if (this._autoReconnect) {
            this.scheduleReconnect();
        }
    }

    private armSessionRefresh(): void {
        this.cancelSessionRefresh();
        this._refreshTimer = setTimeout(() => {
            if (this._status.value === 'active') this.reconnect();
        }, GeminiLiveSegment.SESSION_REFRESH_MS);
    }

    private cancelSessionRefresh(): void {
        if (this._refreshTimer) {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = null;
        }
    }

    private scheduleReconnect(): void {
        if (this._reconnectTimer) return;
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            if (this._status.value === 'active' || this._status.value === 'connecting') return;
            if (!this._autoReconnect) return;
            this.start().catch((err) => console.error('[Gemini2] reconnect failed:', err));
        }, 1500);
    }

    private reconnect(): void {
        this.teardown();
        // teardown() leaves status untouched; start() bails while it's still
        // 'active'/'connecting', so drop to 'idle' first or the reconnect (and
        // the mic re-acquire + wake listener) silently never happens.
        this._status.next('idle');
        this._autoReconnect = true;
        this.start().catch((err) => console.error('[Gemini2] reconnect failed:', err));
    }

    private teardown(): void {
        this._connectAttemptId++;
        this.cancelSessionRefresh();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        try {
            this._session?.close?.();
        } catch {}
        this._session = null;
        this.stopWakeWordListener();
        this.stopMicStreaming();
        this.clearPlayback();
        this.closeOutputContext();
        this._assistantTurnActive = false;
        this._aiSpeaking.next(false);
        this._userSpeaking.next(false);
        this._inputLevel.next(0);
        clearTimeout(this._endTimer);
        this._endTimer = null;
        this._endPending = false;
    }

    // ---- tools ------------------------------------------------------------

    /**
     * End the visit but DON'T close the assistant: mute immediately (so nothing
     * new re-triggers the model), let the spoken goodbye play, then return to
     * the muted "asleep" state where the browser listens for a wake word. The
     * open order panel is closed and the lists refreshed for the next visitor.
     * Idempotent — a second end intent while sleeping is ignored.
     */
    private goToSleep(): void {
        if (this._endPending) return;
        this._endPending = true;
        this.log('visit ending — sleeping after goodbye');
        try {
            this.setMicMuted(true);
        } catch {}
        clearTimeout(this._endTimer);
        this._endTimer = setTimeout(() => {
            this.zone.run(() => {
                // 1) Close the open order panel + clear board state (the page
                //    listens to visitEnded$), then let the overlay reset any
                //    open dialogs / refresh the lists.
                try {
                    this.contextService.notifyVisitEnded();
                } catch {}
                this._endRequested.next();

                // 2) Wipe the transcript so the next visitor starts clean, then
                //    reconnect a fresh session that lands ASLEEP (muted, wake
                //    word armed) so no prior context/name carries over.
                this._transcript.next([]);
                this._lastInputTranscript = '';
                this._assistantTurnActive = false;
                this._endPending = false;
                this._requireWakeWordOnConnect = true;
                this.reconnect();
            });
        }, GeminiLiveSegment.END_GRACE_MS);
    }

    // ---- wake word (local browser SpeechRecognition) ----------------------

    private startWakeWordListener(): void {
        this.stopWakeWordListener();
        const SpeechRecognition =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn('[Gemini2] SpeechRecognition unsupported — wake word disabled');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event: any) => {
            if (!this._micMuted.value) return;
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const text = String(event.results[i][0]?.transcript ?? '').toLowerCase().trim();
                if (GeminiLiveSegment.WAKE_WORDS.some((w) => text.includes(w))) {
                    this.zone.run(() => this.wake(text));
                    return;
                }
            }
        };
        recognition.onerror = (e: any) => {
            if (e?.error === 'no-speech' || e?.error === 'aborted') return;
            console.warn('[Gemini2] SpeechRecognition error:', e?.error);
        };
        recognition.onend = () => {
            // Keep it alive while we're still asleep.
            if (this._status.value === 'active' && this._micMuted.value) {
                try { recognition.start(); } catch {}
            }
        };
        try { recognition.start(); } catch {}
        this._wakeWordRecognition = recognition;
    }

    private stopWakeWordListener(): void {
        if (this._wakeWordRecognition) {
            try { this._wakeWordRecognition.abort(); } catch {}
            this._wakeWordRecognition = null;
        }
    }

    /** Wake word heard: open the mic, refresh the board, then greet. */
    private async wake(heard: string): Promise<void> {
        if (!this._micMuted.value) return;
        this.traceService.log('gemini2_wake_word', { heard }, { detail: `Wake word: "${heard}"` });
        this.stopWakeWordListener();
        this.contextService.notifyVisitStarted();
        this.setMicMuted(false);
        // The board is only baked into systemInstruction at connect, and the
        // lanes are often still loading then. The refresh action now RESOLVES
        // only once both lanes finish loading, so awaiting it guarantees the
        // model gets the CURRENT board before the visitor's first request —
        // otherwise it does a blind lookup that fails until a later refresh.
        await this.dispatchWithTimeout('refresh', {}, 9000);
        this.requestWakeGreeting();
    }

    /**
     * Greet the visitor AND hand the model the current board in the same turn,
     * so it can find/act on orders immediately without a fragile lookup.
     */
    private requestWakeGreeting(): void {
        if (!this._session || this._status.value !== 'active') return;
        let board = '';
        try {
            board = this.contextService.buildPromptContext(this.contextService.getSnapshot());
        } catch {
            board = '';
        }
        try {
            const preface = board
                ? `[CURRENT KIOSK BOARD — authoritative. Read DATA SUMMARY / order lists here to get the #ID. Do NOT search or say you need to look it up if the order is listed. Do NOT read the board aloud.]\n${board}\n\n`
                : '';
            this._session.sendRealtimeInput({
                text:
                    preface +
                    'The visitor just woke you. Greet briefly, then wait. When they name a person: read the board, take the exact #ID, call open_order with order_id (digits only). If more than one match, ask which # — never open a guess. Do not narrate opens.',
            });
        } catch (err) {
            console.error('[Gemini2] wake greeting error:', err);
        }
    }

    private async handleToolCalls(functionCalls: any[]): Promise<void> {
        const responses: { id: string; name: string; response: any }[] = [];

        for (const call of functionCalls) {
            const name = String(call?.name ?? '');
            const id = String(call?.id ?? '');
            const args = call?.args && typeof call.args === 'object' ? call.args : {};

            if (name !== 'page_action') {
                responses.push({ id, name, response: { result: 'error', message: `Unknown function "${name}".` } });
                continue;
            }

            const action = String(args.action ?? '').trim();

            // End-of-visit: this docs-faithful build has no wake word, so any
            // "we're done" intent fully closes the assistant. We handle it
            // locally (never a real page action) and tell the component to
            // deactivate after the model's goodbye finishes.
            if (GeminiLiveSegment.END_ACTIONS.has(action.toLowerCase())) {
                responses.push({
                    id,
                    name,
                    response: {
                        result: 'success',
                        action: 'end_conversation',
                        message: 'Visit ended. Going to sleep — say a wake word ("Yo") to start again.',
                    },
                });
                this.goToSleep();
                continue;
            }
            const { action: _drop, ...restRaw } = args;
            // Models often nest params under `params` — flatten so open_order(id=…) works.
            const nested =
                restRaw?.params && typeof restRaw.params === 'object' && !Array.isArray(restRaw.params)
                    ? restRaw.params
                    : {};
            const { params: _nestedDrop, ...top } = restRaw;
            const rest = { ...nested, ...top };
            const snapshot = this.contextService.getSnapshot();
            const resolved = this.intentRouter.resolvePageAction(snapshot, action, rest);
            const effectiveAction = String(resolved.action ?? action).trim();
            const effectiveParams = resolved.params ?? rest;

            let ok = await this.dispatchWithTimeout(effectiveAction, effectiveParams);
            let refreshed = false;

            // Only refresh+retry when we actually had an ID/query to look up.
            // Empty open_order calls (model forgot order_id) must not churn the board.
            const hasLookupKey = !!(
                effectiveParams?.order_id ||
                effectiveParams?.id ||
                effectiveParams?.query ||
                effectiveParams?.orderId
            );
            if (!ok && hasLookupKey) {
                refreshed = true;
                await this.dispatchWithTimeout('refresh', {}, 9000);
                if (this.isRetrySafeAction(effectiveAction)) {
                    ok = await this.dispatchWithTimeout(effectiveAction, effectiveParams);
                }
            }

            const snap = this.contextService.getSnapshot();
            const dataSummary = String(snap?.dataSummary ?? '');
            const pageLabel = String(snap?.pageLabel ?? '');
            const route = String(snap?.route ?? '');
            const panelOpen = /PANEL OPEN/i.test(dataSummary);

            this.traceService.log(
                'gemini2_page_action',
                { action, effectiveAction, ok, refreshed, effectiveParams, panelOpen },
                {
                    detail: `page_action("${action}" -> "${effectiveAction}") ${ok ? 'OK' : 'FAILED'}${refreshed ? ' (after refresh)' : ''} params=${JSON.stringify(effectiveParams)} panelOpen=${panelOpen}`,
                }
            );

            // For open_* actions, success means the panel is actually open.
            const isOpenAction = /open/i.test(effectiveAction);
            const truthOk = isOpenAction ? ok && panelOpen : ok;

            responses.push({
                id,
                name,
                response: truthOk
                    ? {
                          result: 'success',
                          action: effectiveAction,
                          message: isOpenAction
                              ? `Card is open (${dataSummary.match(/PANEL OPEN[^\n]*/i)?.[0] || 'PANEL OPEN'}). Do NOT say you opened it. Ask the next useful question only.`
                              : `Action "${effectiveAction}" executed.${refreshed ? ' (list was refreshed first)' : ''}`,
                          pageLabel,
                          route,
                          dataSummary,
                          panelOpen: true,
                      }
                    : {
                          result: 'error',
                          action: effectiveAction,
                          message: !hasLookupKey && isOpenAction
                              ? `open_order needs order_id as digits (e.g. "518"). Names are rejected. Read DATA SUMMARY or MATCHES, confirm the #ID, then call open_order with order_id. Do not narrate.`
                              : `${resolved.message || `Could not complete "${effectiveAction}".`} ${isOpenAction ? 'Card NOT open. open_order only accepts numeric order_id. If you passed a name, call find_orders then ask which #ID.' : 'Check DATA SUMMARY and retry.'}`,
                          pageLabel,
                          route,
                          dataSummary,
                          panelOpen: false,
                      },
            });
        }

        try {
            this._session?.sendToolResponse({ functionResponses: responses });
        } catch (err) {
            console.error('[Gemini2] sendToolResponse error:', err);
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Dispatch a page action but never hang: if the page handler doesn't resolve
     * within `ms`, resolve false so we always send a tool response back to the
     * model (a missing tool result is what makes it "check…" then go silent).
     */
    private async dispatchWithTimeout(action: string, params: any, ms = 5000): Promise<boolean> {
        try {
            return await Promise.race([
                this.pageActionService.dispatch(action, params),
                new Promise<boolean>((resolve) => setTimeout(() => resolve(false), ms)),
            ]);
        } catch {
            return false;
        }
    }

    /** Only auto-retry non-mutating lookups so we never double-run ship/label/etc. */
    private isRetrySafeAction(action: string): boolean {
        const a = action.toLowerCase();
        return ['open', 'view', 'select', 'find', 'search', 'show', 'navigate', 'filter', 'scroll'].some(
            (k) => a.includes(k)
        );
    }

    // ---- mic capture ------------------------------------------------------

    private async startMicStreaming(): Promise<void> {
        if (!this._session || this._micStream) return;

        // NOTE: `{ audio: true }` makes the browser silently enable
        // echoCancellation + noiseSuppression + autoGainControl by DEFAULT.
        // We disable EC + NS explicitly (they can gate/attenuate speech and EC
        // can't tell the assistant's voice from the user's on a kiosk) and keep
        // AGC to lift quiet/far speech. Gemini's server VAD does the detection.
        this._micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: true,
            },
        });
        this._inputCtx = new AudioContext();
        if (this._inputCtx.state === 'suspended') {
            await this._inputCtx.resume();
        }
        this.log('mic acquired', {
            ctxRate: this._inputCtx.sampleRate,
            ctxState: this._inputCtx.state,
            tracks: this._micStream.getAudioTracks().map((t) => t.label),
        });
        this._inputSource = this._inputCtx.createMediaStreamSource(this._micStream);
        this._inputProcessor = this._inputCtx.createScriptProcessor(2048, 1, 1);

        this._inputProcessor.onaudioprocess = (event) => {
            if (!this._session || this._status.value !== 'active' || this._micMuted.value) {
                return;
            }
            const channel = event.inputBuffer.getChannelData(0);
            this.updateInputMeter(channel);
            const down = this.downsample(channel, this._inputCtx!.sampleRate, GeminiLiveSegment.INPUT_TARGET_RATE);
            const pcm16 = this.floatTo16BitPCM(down);
            const b64 = this.int16ToBase64(pcm16);
            try {
                this._session.sendRealtimeInput({
                    audio: { data: b64, mimeType: `audio/pcm;rate=${GeminiLiveSegment.INPUT_TARGET_RATE}` },
                });
                if (GeminiLiveSegment.DEBUG) {
                    this._frameCount++;
                    // Track the MAX peak across the whole window (every frame),
                    // not just the frame we happen to log — otherwise a loud word
                    // between log points is invisible.
                    let peak = 0;
                    for (let i = 0; i < channel.length; i++) {
                        const a = Math.abs(channel[i]);
                        if (a > peak) peak = a;
                    }
                    if (peak > this._winPeak) this._winPeak = peak;
                    if (this._frameCount === 1 || this._frameCount % 50 === 0) {
                        this.log(`audio frames sent: ${this._frameCount}`, {
                            windowPeak: this._winPeak.toFixed(3),
                            muted: this._micMuted.value,
                        });
                        this._winPeak = 0;
                    }
                }
            } catch (err) {
                this.log('sendRealtimeInput(audio) threw', err);
            }
        };

        this._inputSource.connect(this._inputProcessor);
        this._inputProcessor.connect(this._inputCtx.destination);
        this.log('mic streaming started (ScriptProcessor 2048)');
    }

    private stopMicStreaming(): void {
        if (this._inputProcessor) {
            try { this._inputProcessor.disconnect(); } catch {}
            this._inputProcessor.onaudioprocess = null as any;
            this._inputProcessor = null;
        }
        if (this._inputSource) {
            try { this._inputSource.disconnect(); } catch {}
            this._inputSource = null;
        }
        if (this._inputCtx) {
            try { this._inputCtx.close(); } catch {}
            this._inputCtx = null;
        }
        if (this._micStream) {
            this._micStream.getTracks().forEach((t) => t.stop());
            this._micStream = null;
        }
    }

    private flushAudioStream(): void {
        try {
            this.log('-> audioStreamEnd (mic paused)');
            this._session?.sendRealtimeInput({ audioStreamEnd: true });
        } catch {}
    }

    private updateInputMeter(buffer: Float32Array): void {
        let sum = 0;
        let peak = 0;
        for (let i = 0; i < buffer.length; i++) {
            const v = buffer[i];
            sum += v * v;
            const a = Math.abs(v);
            if (a > peak) peak = a;
        }
        const rms = Math.sqrt(sum / Math.max(1, buffer.length));

        // Emit ~every 3rd frame (~7/sec) inside the Angular zone so the HUD
        // meter re-renders (this callback runs outside Angular's zone).
        if ((++this._meterFrame % 3) !== 0) return;
        const speaking = this._userSpeaking.value;
        this.zone.run(() => {
            this._inputLevel.next(peak);
            if (!speaking && rms > GeminiLiveSegment.SPEAK_ON_RMS) {
                this._userSpeaking.next(true);
                this.log('userSpeaking -> TRUE', { rms: rms.toFixed(3), peak: peak.toFixed(3) });
            } else if (speaking && rms < GeminiLiveSegment.SPEAK_OFF_RMS) {
                this._userSpeaking.next(false);
            }
        });
    }

    // ---- output playback --------------------------------------------------

    private ensureOutputContext(): void {
        if (!this._outputCtx) {
            this._outputCtx = new AudioContext({ sampleRate: GeminiLiveSegment.OUTPUT_RATE });
            this._nextPlaybackTime = this._outputCtx.currentTime;
        }
        if (this._outputCtx.state === 'suspended') {
            this._outputCtx.resume();
        }
    }

    private closeOutputContext(): void {
        if (!this._outputCtx) return;
        try { this._outputCtx.close(); } catch {}
        this._outputCtx = null;
        this._nextPlaybackTime = 0;
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
    }

    private playPcm16(base64Data: string, mimeType: string): void {
        this.ensureOutputContext();
        if (!this._outputCtx) return;

        const rateMatch = mimeType.match(/rate=(\d+)/i);
        const rate = rateMatch ? Number(rateMatch[1]) : GeminiLiveSegment.OUTPUT_RATE;
        const bytes = this.base64ToBytes(base64Data);
        const sampleCount = Math.floor(bytes.byteLength / 2);
        if (sampleCount <= 0) return;

        const buffer = this._outputCtx.createBuffer(1, sampleCount, rate);
        const channel = buffer.getChannelData(0);
        for (let i = 0; i < sampleCount; i++) {
            const lo = bytes[i * 2];
            const hi = bytes[i * 2 + 1];
            let val = (hi << 8) | lo;
            if (val >= 0x8000) val -= 0x10000;
            channel[i] = Math.max(-1, Math.min(1, val / 32768));
        }

        const src = this._outputCtx.createBufferSource();
        src.buffer = buffer;
        src.connect(this._outputCtx.destination);
        const now = this._outputCtx.currentTime;
        const when = Math.max(now, this._nextPlaybackTime);
        src.start(when);
        this._nextPlaybackTime = when + buffer.duration;
        this._activeSources.add(src);
        src.onended = () => this._activeSources.delete(src);
        this._aiSpeaking.next(true);
        this.schedulePlaybackEnd();
    }

    private clearPlayback(): void {
        for (const src of this._activeSources) {
            try { src.stop(); } catch {}
            try { src.disconnect(); } catch {}
        }
        this._activeSources.clear();
        if (this._outputCtx) {
            this._nextPlaybackTime = this._outputCtx.currentTime;
        }
        this._aiSpeaking.next(false);
    }

    private schedulePlaybackEnd(): void {
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
        if (!this._outputCtx) return;
        const ms = Math.max(
            0,
            (this._nextPlaybackTime + GeminiLiveSegment.POST_PLAYBACK_GAP_S - this._outputCtx.currentTime) * 1000
        );
        this._playbackEndTimer = setTimeout(() => {
            this._playbackEndTimer = null;
            if (this._activeSources.size === 0) {
                this._aiSpeaking.next(false);
            }
        }, ms + 20);
    }

    // ---- helpers ----------------------------------------------------------

    private canStartOnCurrentPage(snapshot: any): boolean {
        const pageLabel = String(snapshot?.pageLabel ?? '').trim().toLowerCase();
        const route = String(snapshot?.route ?? '').toLowerCase();
        const actionCount = Array.isArray(snapshot?.actions) ? snapshot.actions.length : 0;
        if (actionCount > 0) return true;
        if (['receiving', 'shipping', 'printer', 'mailbox', 'email'].some((k) => pageLabel.includes(k))) return true;
        if (['/receiving', '/shipping', '/printer-view', '/mailbox'].some((k) => route.includes(k))) return true;
        return false;
    }

    private addTranscript(role: 'user' | 'assistant' | 'system', text: string): void {
        const next = [...this._transcript.value, { role, text, at: new Date().toISOString() }].slice(-40);
        this._transcript.next(next);
    }

    private appendAssistant(fragment: string): void {
        const t = [...this._transcript.value];
        for (let i = t.length - 1; i >= 0; i--) {
            if (t[i].role === 'assistant') {
                const existing = t[i].text;
                const merged = fragment.startsWith(existing) ? fragment : existing + ' ' + fragment;
                t[i] = { ...t[i], text: merged };
                this._transcript.next(t);
                return;
            }
        }
        this.addTranscript('assistant', fragment);
    }

    private downsample(input: Float32Array, inputRate: number, targetRate: number): Float32Array {
        if (inputRate === targetRate) return input;
        const ratio = inputRate / targetRate;
        const newLength = Math.round(input.length / ratio);
        const result = new Float32Array(newLength);
        let offsetResult = 0;
        let offsetBuffer = 0;
        while (offsetResult < result.length) {
            const nextOffset = Math.round((offsetResult + 1) * ratio);
            let accum = 0;
            let count = 0;
            for (let i = offsetBuffer; i < nextOffset && i < input.length; i++) {
                accum += input[i];
                count++;
            }
            result[offsetResult] = count > 0 ? accum / count : 0;
            offsetResult++;
            offsetBuffer = nextOffset;
        }
        return result;
    }

    private floatTo16BitPCM(input: Float32Array): Int16Array {
        const out = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    private int16ToBase64(data: Int16Array): string {
        const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
    }

    private base64ToBytes(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    }
}
